import { parseInt } from 'lodash'

const parseEnvInt = (key: string, def: number): number => {
  const text = process.env[key]
  if (!text) {
    return def
  }
  try {
    const asNumber = parseInt(text)
    return asNumber
  } catch (err) {}
  return def
}

export const AZURE = {
  clientId: process.env.AZURE_AD_CLIENT_ID ?? '',
  clientSecret: process.env.AZURE_AD_CLIENT_SECRET ?? '',
  tenantId: process.env.AZURE_AD_TENANT_ID ?? '',
  redirect_uri: `${process.env.NEXTAUTH_URL}/api/auth/callback/azure-ad`,
  scopes: {
    basic: ['email', 'openid', 'profile', 'offline_access'],
    custom: [
      'Chat.ReadBasic', // List chats in Dropdown
      'User.Read', // Sign in to the app and read signed-in user details
      'User.ReadBasic.All', // Get other user's profile photo
    ],
  },
  getScopes: () => [...AZURE.scopes.basic, ...AZURE.scopes.custom].join(' '),
}

// Debug environment variables only on server-side
if (typeof window === 'undefined') {
  console.log('[ENV DEBUG] Node Env:', process.env.NODE_ENV)
}

export const FEATURE_FLAGS = {
  PREMIUM: {
    enabled: true,
  },
  // Only bypass if explicitly enabled via env var, not automatically in development
  BYPASS_SUBSCRIPTION_CHECKS: process.env.BYPASS_SUBSCRIPTION_CHECKS === 'true',
}

export const SUBSCRIPTION_FEATURES = {
  MAX_ATTACHMENTS: 'max_attachments', // Maximum number of attachments per message
  RECURRING_REMINDERS: 'recurring_reminders', // Ability to set recurring reminders
  RETENTION_DAYS: 'retention_days', // Days to keep sent messages visible
  ACTIVE_REMINDERS: 'active_reminders', // Maximum number of active reminders at once
  UNLIMITED_REMINDERS: 'unlimited_reminders', // No limit on number of reminders
  TEMPLATES: 'template_creation', // Ability to create and use message templates
}

export const SUBSCRIPTION_PLANS = {
  FREE: {
    features: [], // No premium features in free plan
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 0, // No attachments in free plan
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 7, // Only 7 days retention
      [SUBSCRIPTION_FEATURES.ACTIVE_REMINDERS]: 5, // Only 5 active reminders
    },
  },
  PRO: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_REMINDERS,
      SUBSCRIPTION_FEATURES.UNLIMITED_REMINDERS,
      SUBSCRIPTION_FEATURES.TEMPLATES,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 10,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 90,
      [SUBSCRIPTION_FEATURES.ACTIVE_REMINDERS]: 999999, // Effectively unlimited
    },
    pricing: {
      monthly: {
        price: 3,
        currency: 'USD',
        interval: 'month',
      },
      yearly: {
        price: 20,
        currency: 'USD',
        interval: 'year',
      },
    },
  },
  TEAM: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_REMINDERS,
      SUBSCRIPTION_FEATURES.UNLIMITED_REMINDERS,
      SUBSCRIPTION_FEATURES.TEMPLATES,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 10,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 90,
      [SUBSCRIPTION_FEATURES.ACTIVE_REMINDERS]: 999999, // Effectively unlimited
    },
    pricing: {
      monthly: {
        price: 2,
        currency: 'USD',
        interval: 'month',
        minSeats: 2,
      },
      yearly: {
        price: 15,
        currency: 'USD',
        interval: 'year',
        minSeats: 2,
      },
    },
  },
  GIFT: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_REMINDERS,
      SUBSCRIPTION_FEATURES.UNLIMITED_REMINDERS,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 5,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 30,
      [SUBSCRIPTION_FEATURES.ACTIVE_REMINDERS]: 100,
    },
  },
}

export const STORAGE_CONFIG = {
  AZURE: {
    connectionString: process.env.AZURE_STORAGE_CONNECTION_STRING || '',
    containerName: process.env.AZURE_STORAGE_CONTAINER_NAME || 'attachments',
    accountName: process.env.AZURE_STORAGE_ACCOUNT_NAME || '',
    accountKey: process.env.AZURE_STORAGE_ACCOUNT_KEY || '',
    // Use different container for development to avoid conflicts
    devConnectionString:
      process.env.DEV_AZURE_STORAGE_CONNECTION_STRING ||
      process.env.AZURE_STORAGE_CONNECTION_STRING ||
      '',
    devContainerName: process.env.DEV_AZURE_STORAGE_CONTAINER_NAME || 'dev-attachments',
  },
  // Always use Azure storage regardless of environment
  useAzure: true,
}

export const APP_NAME = 'teams-reminder'
export const VERSION = process.env.VERSION ?? 'unknown'
export const DEVELOPMENT = process.env.NODE_ENV === 'development'
export const MONGO_URI = process.env.MONGODB_URI ?? ''
export const TASK_AUTH_SECRET = process.env.TASK_AUTH_SECRET || ''

// Azure Communication Services configuration
// For backward compatibility, check both new and old env var names
export const COMMUNICATION_SERVICES_CONNECTION_STRING =
  process.env.COMMUNICATION_SERVICES_CONNECTION_STRING ||
  process.env.AZURE_COMMUNICATION_SERVICE_CONNECTION_STRING ||
  ''

export const COMMUNICATION_SERVICES_SENDER_ADDRESS =
  process.env.COMMUNICATION_SERVICES_SENDER_ADDRESS ||
  process.env.AZURE_COMMUNICATION_SERVICE_SENDER_ADDRESS ||
  'noreply@teamsreminder.com'

export const COMMUNICATION_SERVICES_REPLY_TO =
  process.env.COMMUNICATION_SERVICES_REPLY_TO || 'support+reminders@appfluence.com'

// Enable email service in development based on available configurations
export const DEV_EMAIL_SERVICE =
  process.env.NODE_ENV === 'production' ||
  !!COMMUNICATION_SERVICES_CONNECTION_STRING

// Azure Communication Services is the only email provider
export const USE_AZURE_COMMUNICATION_SERVICE = !!COMMUNICATION_SERVICES_CONNECTION_STRING
export const KEEP_ALIVE_REFRESH_TOKEN_INTERVAL = parseEnvInt(
  'KEEP_ALIVE_REFRESH_TOKEN_INTERVAL',
  5 * 60
) //5 min in secs
export const KEEP_ALIVE_REFRESH_TOKEN_LIMIT = parseEnvInt(
  'KEEP_ALIVE_REFRESH_TOKEN_LIMIT',
  10
) //10 users

const nextAuth = process.env.NEXTAUTH_URL || ''
export const SAFE_COOKIE = nextAuth.indexOf('https://') == 0
export const TEAMS_APP_ID = process.env.TEAMS_APP_ID ?? ''

// Determine the correct entity ID based on environment
const getTeamsEntityId = () => {
  // Use environment-specific entity ID if specifically provided
  if (process.env.TEAMS_ENTITY_ID) {
    return process.env.TEAMS_ENTITY_ID;
  }
  
  // Otherwise default based on NODE_ENV
  const isDev = process.env.NODE_ENV === 'development';
  return isDev ? 'dev.teamsreminder.teams.main' : 'com.teamsreminder.teams.main';
};

export const TEAMS_ENTITY_ID = getTeamsEntityId();

export const TEAMS = {
  // Standard deep link for dashboard
  DEEP_LINK_DASHBOARD: `https://teams.microsoft.com/l/entity/${TEAMS_APP_ID}/${TEAMS_ENTITY_ID}`,
}
